import { Box, Breadcrumbs, IconButton, Link, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { ChangeEvent, useEffect, useMemo, useRef } from "react";

import RefreshButton from "../../../../../common/components/refresh_button";
import PrimaryButton from "../../../../../common/components/primary_button";
import {
  PageTopBar,
  ThreeSlotLayout,
} from "../../../../../common/components/page_top_bar";
import FormContainer from "../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../common/components/default_form_input";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";

import { FormDatePickerInput } from "../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DropDownFuncionario } from "../../../../../common/components/dropdown_funcionario";
import { FormRadioGroupInput } from "../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DropDownCentroCusto } from "../../../../../common/components/dropdown_centro_custo";

import { GenerateItemMedicaoColumns } from "./columns/item_medicao";

import { Controller } from "react-hook-form";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import { useMedicaoTerceirizacaoCadastroViewModel } from "../../viewModels/medicao_terceirizacao_view_model";
import { DropDownContrato } from "../../../../../common/components/dropdown_contrato";
import { DropDownSubconta } from "../../../../../common/components/dropdown_subconta";
import { ConfigPagamentoModal } from "./components/config_pagamento_modal";
import { ItemMedicaoTerceirizacaoLista } from "../../../domain/models/config_item_mediacao_lista";
import { FormMoneyMaskedInput } from "../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { BackButton } from "../../../../../common/components/back_button";
import { ItensModal } from "./components/itens_modal";
import { ItemMedicaoTerceirizacaoForm } from "../../schemas/Medicao/itens_medicao_terceirizacao";
import BottomBar from "../../../../../common/components/bottom_bar";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { isValidId } from "../../../../../common/utils/utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const MedicaoTerceirizacao_Cadastro = () => {
  const viewModel = useMedicaoTerceirizacaoCadastroViewModel();
  const idContratoSelecionado = viewModel.hookForm.watch("IdContrato");

 const statusValue = viewModel.hookForm.watch("Status");
  const prevStatusRef = useRef(statusValue);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevStatusRef.current = statusValue;
      return;
    }

    if (statusValue !== undefined && statusValue !== prevStatusRef.current) {
      prevStatusRef.current = statusValue;

      if (viewModel.isRevertingStatus.current) {
        viewModel.isRevertingStatus.current = false; 
        return; 
      }

      setTimeout(() => {
        viewModel.hookForm.handleSubmit(viewModel.handleMedicaoSubmit)();
      }, 50);
    }
  }, [statusValue, viewModel]);

  useEffect(() => {
    if (idContratoSelecionado) {
      viewModel.setIdContrato(idContratoSelecionado);
    }
  }, [idContratoSelecionado, viewModel]);

  const ContratoItemMedicaoListaColumns = useMemo(() => {
    return GenerateItemMedicaoColumns({
      onActionEditar: viewModel.handleEdicaoItemMedicaoOpenState,
      onActionDeletar: viewModel.handleDeleteItemMedicaoModalOpenState,
      validButton: viewModel.validFormulario,
    });
  }, [viewModel]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          leftSlot={
            <div className="flex flex-row items-center h-full gap-6">
              <div className="flex flex-row items-center gap-3">
                <span className="font-semibold -translate-y-[2px] whitespace-nowrap">
                  Status:
                </span>
                <div className="flex items-center [&_.MuiFormGroup-row]:flex-nowrap">
                  <FormRadioGroupInput
                    name="Status"
                    arrangement="horizontal"
                    boldOptionsLabel={true}
                    options={viewModel.statusOptions}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
              </div>
              <div className="w-px h-8 bg-gray-300 hidden sm:block"></div>
              <div className="w-[150px] mt-1.5">
                <DefaultFormInput
                  name="NrMedicaoTerceirizacao"
                  label="Nº Medição"
                  readOnly={true}
                  reactHookForm={viewModel.hookForm}
                />
              </div>
            </div>
          }
          rightSlot={<BackButton onClick={viewModel.handleVoltarMedicao} />}
        />
      </PageTopBar>
      <form>
        <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Terceirização
            </Link>
            <Typography className="text-primary-green">
              Medição Lista
            </Typography>
            <Typography className="text-primary-green">
              Cadastro de Medição
            </Typography>
          </Breadcrumbs>
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">
                Cadastro de Medição
              </h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>
          </div>
          <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
            
            <FormContainer title={"Informações Gerais"} arrangement="fullRow">
              <div className="flex flex-col w-full gap-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DropDownContrato
                    name="IdContrato"
                    label="Contrato"
                    readOnly={viewModel.validFormulario}
                    required={true}
                    reactHookForm={viewModel.hookForm}
                  />
                  <DropDownCentroCusto
                    name="IdCentroCusto"
                    label="Centro de Custo"
                    IdContrato={viewModel.IdContrato}
                    readOnly={true}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DefaultFormInput
                    name="Descricao"
                    label="Descrição"
                    readOnly={viewModel.validFormulario}
                    reactHookForm={viewModel.hookForm}
                  />
                  <FormDatePickerInput
                    name="DataMedicao"
                    label="Data da Medição"
                    readOnly={viewModel.validFormulario}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormDatePickerInput
                    name="DataDe"
                    label="Data De"
                    readOnly={viewModel.validFormulario}
                    reactHookForm={viewModel.hookForm}
                  />
                  <FormDatePickerInput
                    name="DataAte"
                    label="Data Ate"
                    readOnly={viewModel.validFormulario}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DefaultFormInput
                    name="NroNotaFiscal"
                    label="Nº Nota Fiscal"
                    readOnly={viewModel.validFormulario}
                    reactHookForm={viewModel.hookForm}
                  />
                  <Box
                    sx={{
                      position: "relative",
                      "& .MuiAutocomplete-popper": {
                        position: "absolute !important",
                        top: "100% !important",
                        bottom: "auto !important",
                        transform: "none !important",
                        zIndex: 9999,
                      },
                      "& .MuiAutocomplete-listbox": {
                        maxHeight: "200px !important",
                      },
                    }}
                  >
                    <DropDownSubconta
                      name="IdSubconta"
                      label="Subconta"
                      readOnly={viewModel.validFormulario}
                      reactHookForm={viewModel.hookForm}
                    />
                  </Box>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DropDownFuncionario
                    name="IdFuncionarioResp"
                    label="Responsável"
                    readOnly={viewModel.validFormulario}
                    reactHookForm={viewModel.hookForm}
                  />
                  <DefaultFormInput
                    name="InformacoesGerais"
                    readOnly={viewModel.validFormulario}
                    label="Informações Gerais"
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
              </div>
            </FormContainer>

            {/* SEÇÃO ItemMedicao */}
            <span>Itens da Medição</span>
            {viewModel.validFormulario === false && (
              <div className="w-40 mt-4 mb-4">
                <PrimaryButton
                  type="button"
                  onClick={viewModel.handleItemMedicao}
                  buttonColor={"primary-green"}
                >
                  Novo
                </PrimaryButton>
              </div>
            )}
            {viewModel.ListaItemMedicao.length > 0 ? (
              <>
                <StyledDataGrid
                  tableName="Contrato Serviço"
                  disableColumnMenu={false}
                  getRowId={(row: ItemMedicaoTerceirizacaoLista) =>
                    row.IdItemMedicaoTerceirizacao
                  }
                  columns={ContratoItemMedicaoListaColumns}
                  loading={viewModel.loadingItemMedicao}
                  rows={viewModel.ListaItemMedicao}
                  rowCount={viewModel.paginationItemMedicao.totalItems}
                  sx={{
                    "& .status-header": {
                      backgroundColor: "#AFDFD5",
                      color: "#000",
                    },
                  }}
                  pinnedColumns={{
                    right: ["IdItemMedicaoTerceirizacao"],
                  }}
                  autoHeight={true}
                  textSize="medio"
                  getRowHeight={() => "auto"}
                  pageSizeOptions={[10]}
                  paginationMode="server"
                  paginationModel={{
                    page: viewModel.paginationItemMedicao.page - 1,
                    pageSize: viewModel.paginationItemMedicao.rowsPerPage,
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                  <FormMoneyMaskedInput
                    name={"ValorRetencao"}
                    label={"Retenção"}
                    readOnly={viewModel.validFormulario}
                    largeNumbers={true}
                    reactHookForm={viewModel.hookForm}
                  />
                  <FormMoneyMaskedInput
                    name={"Acrescimo"}
                    label={"Acréscimo"}
                    readOnly={viewModel.validFormulario}
                    largeNumbers={true}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <FormMoneyMaskedInput
                    name={"Imposto"}
                    label={"Imposto"}
                    readOnly={viewModel.validFormulario}
                    largeNumbers={true}
                    reactHookForm={viewModel.hookForm}
                  />
                  <FormMoneyMaskedInput
                    name={"Desconto"}
                    label={"Desconto"}
                    readOnly={viewModel.validFormulario}
                    largeNumbers={true}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <FormMoneyMaskedInput
                    name={"ValorTotalCalculado"}
                    label={"Valor Total"}
                    largeNumbers={true}
                    readOnly={true}
                    reactHookForm={viewModel.hookForm}
                  />
                  <FormMoneyMaskedInput
                    name={"SubTotal"}
                    label={"SubTotal"}
                    largeNumbers={true}
                    readOnly={true}
                    reactHookForm={viewModel.hookForm}
                  />
                </div>
              </>
            ) : (
              <></>
            )}

            <FormContainer title={"Anexar Arquivos"} arrangement="fullRow">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Controller
                    name="Arquivo1"
                    control={viewModel.hookFormArquivoGenericos.control}
                    render={({ field }) =>
                      viewModel.selectedArquivoGenericos &&
                      viewModel.selectedArquivoGenericos.Arquivo1 &&
                      viewModel.selectedArquivoGenericos.Arquivo1.length > 0 ? (
                        <div className="w-full flex flex-row justify-between items-center">
                          <span
                            className="cursor-pointer"
                            onMouseDown={() =>
                              viewModel.handleDownload(
                                viewModel.selectedArquivoGenericos?.NomeArquivo1!,
                                viewModel.selectedArquivoGenericos?.Arquivo1!,
                              )
                            }
                          >
                            {viewModel.selectedArquivoGenericos?.NomeArquivo1 &&
                            viewModel.selectedArquivoGenericos.NomeArquivo1.length > 20
                              ? `${viewModel.selectedArquivoGenericos.NomeArquivo1.substring(0, 20)}...`
                              : viewModel.selectedArquivoGenericos?.NomeArquivo1}
                          </span>
                          <IconButton
                            onClick={() =>
                              viewModel.handleArquivoDeleteOpenState(
                                "limparArquivo1",
                                "Arquivo1",
                                true,
                              )
                            }
                          >
                            <DynamicIcon
                              nameIcon="DeleteForeverRounded"
                              size={25}
                              className="text-red-500"
                            />
                          </IconButton>
                        </div>
                      ) : (
                        <PrimaryButton
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          disabled={viewModel.saving}
                          className="w-full"
                          startIcon={
                            <DynamicIcon
                              nameIcon="Download"
                              size={25}
                              className="text-white"
                            />
                          }
                        >
                          {field.value ? "Selecionado" : "Arquivo.1"}
                          <VisuallyHiddenInput
                            type="file"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                field.onChange(file);
                                viewModel.hookFormArquivoGenericos.setValue(
                                  "NomeArquivo1",
                                  file.name,
                                );
                              }
                            }}
                            multiple={false}
                          />
                        </PrimaryButton>
                      )
                    }
                  />
                </div>
                <div>
                  <Controller
                    name="Arquivo2"
                    control={viewModel.hookFormArquivoGenericos.control}
                    render={({ field }) =>
                      viewModel.selectedArquivoGenericos &&
                      viewModel.selectedArquivoGenericos.Arquivo2 &&
                      viewModel.selectedArquivoGenericos.Arquivo2.length > 0 ? (
                        <div className="w-full flex flex-row justify-between items-center">
                          <span
                            className="cursor-pointer"
                            onMouseDown={() =>
                              viewModel.handleDownload(
                                viewModel.selectedArquivoGenericos?.NomeArquivo2!,
                                viewModel.selectedArquivoGenericos?.Arquivo2!,
                              )
                            }
                          >
                            {viewModel.selectedArquivoGenericos?.NomeArquivo2 &&
                            viewModel.selectedArquivoGenericos.NomeArquivo2.length > 20
                              ? `${viewModel.selectedArquivoGenericos.NomeArquivo2.substring(0, 20)}...`
                              : viewModel.selectedArquivoGenericos?.NomeArquivo2}
                          </span>
                          <IconButton
                            onClick={() =>
                              viewModel.handleArquivoDeleteOpenState(
                                "limparArquivo2",
                                "Arquivo2",
                                true,
                              )
                            }
                          >
                            <DynamicIcon
                              nameIcon="DeleteForeverRounded"
                              size={25}
                              className="text-red-500"
                            />
                          </IconButton>
                        </div>
                      ) : (
                        <PrimaryButton
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          disabled={viewModel.saving}
                          className="w-full"
                          startIcon={
                            <DynamicIcon
                              nameIcon="Download"
                              size={25}
                              className="text-white"
                            />
                          }
                        >
                          {field.value ? "Selecionado" : "Arquivo.2"}
                          <VisuallyHiddenInput
                            type="file"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                field.onChange(file);
                                viewModel.hookFormArquivoGenericos.setValue(
                                  "NomeArquivo2",
                                  file.name,
                                );
                              }
                            }}
                            multiple={false}
                          />
                        </PrimaryButton>
                      )
                    }
                  />
                </div>
                <div>
                  <Controller
                    name="Arquivo3"
                    control={viewModel.hookFormArquivoGenericos.control}
                    render={({ field }) =>
                      viewModel.selectedArquivoGenericos &&
                      viewModel.selectedArquivoGenericos.Arquivo3 &&
                      viewModel.selectedArquivoGenericos.Arquivo3.length > 0 ? (
                        <div className="w-full flex flex-row justify-between items-center">
                          <span
                            className="cursor-pointer"
                            onMouseDown={() =>
                              viewModel.handleDownload(
                                viewModel.selectedArquivoGenericos?.NomeArquivo3!,
                                viewModel.selectedArquivoGenericos?.Arquivo3!,
                              )
                            }
                          >
                            {viewModel.selectedArquivoGenericos?.NomeArquivo3 &&
                            viewModel.selectedArquivoGenericos.NomeArquivo3.length > 20
                              ? `${viewModel.selectedArquivoGenericos.NomeArquivo3.substring(0, 20)}...`
                              : viewModel.selectedArquivoGenericos?.NomeArquivo3}
                          </span>
                          <IconButton
                            onClick={() =>
                              viewModel.handleArquivoDeleteOpenState(
                                "limparArquivo3",
                                "Arquivo3",
                                true,
                              )
                            }
                          >
                            <DynamicIcon
                              nameIcon="DeleteForeverRounded"
                              size={25}
                              className="text-red-500"
                            />
                          </IconButton>
                        </div>
                      ) : (
                        <PrimaryButton
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          disabled={viewModel.saving}
                          className="w-full"
                          startIcon={
                            <DynamicIcon
                              nameIcon="Download"
                              size={25}
                              className="text-white"
                            />
                          }
                        >
                          {field.value ? "Selecionado" : "Arquivo.3"}
                          <VisuallyHiddenInput
                            type="file"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                field.onChange(file);
                                viewModel.hookFormArquivoGenericos.setValue(
                                  "NomeArquivo3",
                                  file.name,
                                );
                              }
                            }}
                            multiple={false}
                          />
                        </PrimaryButton>
                      )
                    }
                  />
                </div>
              </div>
            </FormContainer>
          </div>
        </div>

        <BottomBar>
          <div className="flex w-full h-full flex-row items-center justify-between gap-4 p-4">
            <div>
              {isValidId(viewModel.IdMedicao) && (
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => viewModel.handleOpenChangeHistory(null, null)}
                >
                  <span className="text-xs">Histórico de alterações</span>
                </PrimaryButton>
              )}
            </div>
            <div className="flex flex-row items-center justify-end gap-4">
              {Number(statusValue) === 3 && (
                <PrimaryButton
                  buttonColor="purple"
                  startIcon={
                    <DynamicIcon
                      nameIcon="AddRounded"
                      size={25}
                      className="text-white"
                    />
                  }
                  onClick={() =>
                    viewModel.handleCadastroConfigPagamentoOpenState(true)
                  }
                >
                  Configurar Pagamento
                </PrimaryButton>
              )}
              {viewModel.validFormulario === false && (
                <>
                  <div
                    className="cursor-pointer rounded-[4px] p-2 font-semibold hover:bg-hover-light-gray dark:hover:bg-border-dark-gray"
                    onClick={viewModel.handleVoltarMedicao}
                  >
                    <h1 className="text-sm text-black dark:text-white">
                      Cancelar
                    </h1>
                  </div>

                  <div className="w-[130px]">
                    {viewModel.saving ? (
                      <PrimaryButton>Salvando...</PrimaryButton>
                    ) : (
                      <PrimaryButton
                        type="submit"
                        onClick={viewModel.hookForm.handleSubmit(
                          viewModel.handleMedicaoSubmit,
                        )}
                        startIcon={
                          <DynamicIcon
                            nameIcon="CheckRounded"
                            size={25}
                            className="text-white"
                          />
                        }
                      >
                        Salvar
                      </PrimaryButton>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </BottomBar>

      </form>

      <ChangeHistoryModal changeHistoryHandler={viewModel.changeHistory} />
      <ConfigPagamentoModal
        open={viewModel.configPagamentoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedConfigPag={viewModel.selectedConfigPagamento}
        onClose={() => viewModel.handleCadastroConfigPagamentoOpenState(false)}
        editmod={false}
        onSubmit={viewModel.handleConfigPagamentolSubmit}
        onSubmitDeletar={viewModel.handleDeleteConfigPagamentoModalOpenState}
      />

      <ItensModal
        open={viewModel.openItemMedicao}
        IdContrato={viewModel.IdContrato}
        IdMedicaoTerceirizacao={viewModel.IdMedicao}
        saving={viewModel.saving}
        selectedItem={
          (viewModel.selectedItemMedicao ??
            null) as unknown as ItemMedicaoTerceirizacaoForm | null
        }
        onSubmit={viewModel.handleItemMedicaoSubmit}
        validFormulario={viewModel.validFormulario}
        onClose={() => viewModel.setOpenItemMedicao(false)}
        onOpenChangeHistory={(id, tabelaTipo) =>
          viewModel.handleOpenChangeHistory(id, tabelaTipo)
        }
      />

      {/* DIALOGS */}
      <DeleteDialog
        open={viewModel.openDeleteArquivoModal}
        saving={viewModel.saving}
        onClose={() =>
          viewModel.handleArquivoDeleteOpenState(null, null, false)
        }
        title={`Você realmente deseja excluir este arquivo?`}
        onDelete={viewModel.handleConfirmarDeletarArquivo}
      />
      <DeleteDialog
        open={viewModel.configPagamentoDeleteModal}
        saving={viewModel.saving}
        onClose={() =>
          viewModel.handleDeleteConfigPagamentoModalOpenState(false)
        }
        title={`Você realmente deseja excluir esta configuração?`}
        onDelete={viewModel.handleDeleteConfirmaConfigPagamento}
      />
      <DeleteDialog
        open={viewModel.itemMedicaoDeleteModal}
        saving={viewModel.saving}
        onClose={() =>
          viewModel.handleDeleteItemMedicaoModalOpenState(null, false)
        }
        title={`Você realmente deseja excluir esta configuração?`}
        onDelete={viewModel.handleDeleteConfirmaItemMedicao}
      />
    </>
  );
};